var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import { merge } from 'lodash';
import { toast } from 'react-toastify';
import { z } from 'zod';
import imageCompression from 'browser-image-compression';
import localforage from 'localforage';
import __ from '@/helpers/translation';
import apiRoutes from '@/actions/api';
localforage.config({
    driver: localforage.INDEXEDDB,
});
/**
 * Zod schema for validating an image object.
 */
var imageSchema = z.object({
    name: z.string().min(1, 'Image name can\'t be an empty string.'),
    dataUri: z.string().url('Invalid data URI.'),
    answerId: z.number().int().positive('Answer ID must be a positive integer.'),
    hash: z.string().min(1, 'Hash can\'t be an empty string.'),
    pushedToBucket: z.boolean().default(false),
});
/**
 * Zustand store for managing images.
 *
 * This store uses the `persist` middleware to save the state to `localStorage` and the `immer` middleware to handle immutable state updates.
 * It also contains methods for compressing and decompressing images, calculating SHA512 hashes, adding and removing images, and validating image data.
 *
 * @returns {ImageStore} The Zustand store for images.
 */
export var useImageStore = create()(persist(immer(function (set, get) { return ({
    images: new Map(),
    isUploading: false,
    compressImage: function (image) { return __awaiter(void 0, void 0, void 0, function () {
        var compressedFile, dataUri, error_1;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    _a.trys.push([0, 3, , 4]);
                    return [4 /*yield*/, imageCompression(image, {
                            maxSizeMB: 1,
                            maxWidthOrHeight: 1920,
                            useWebWorker: true,
                        })];
                case 1:
                    compressedFile = _a.sent();
                    return [4 /*yield*/, imageCompression.getDataUrlFromFile(compressedFile)];
                case 2:
                    dataUri = _a.sent();
                    return [2 /*return*/, dataUri];
                case 3:
                    error_1 = _a.sent();
                    console.error("Error during compression or conversion:", error_1);
                    throw error_1;
                case 4: return [2 /*return*/];
            }
        });
    }); },
    decompressImage: function (base64, filename) {
        var arr = base64.split(',');
        var mimeMatch = arr[0].match(/:(.*?);/);
        var mime = mimeMatch ? mimeMatch[1] : '';
        var bstr = atob(arr[1]); // Decode base64 to binary string
        var n = bstr.length;
        var u8arr = new Uint8Array(n);
        while (n--) {
            u8arr[n] = bstr.charCodeAt(n);
        }
        return new File([u8arr], filename, { type: mime });
    },
    calculateHash: function (contents) { return __awaiter(void 0, void 0, void 0, function () {
        var msgBuffer, hashBuffer, hashArray, hashHex;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    msgBuffer = new TextEncoder().encode(contents);
                    return [4 /*yield*/, crypto.subtle.digest('SHA-512', msgBuffer)];
                case 1:
                    hashBuffer = _a.sent();
                    hashArray = Array.from(new Uint8Array(hashBuffer));
                    hashHex = hashArray.map(function (b) { return b.toString(16).padStart(2, '0'); }).join('');
                    return [2 /*return*/, hashHex];
            }
        });
    }); },
    addImage: function (image, answerId) { return __awaiter(void 0, void 0, void 0, function () {
        var dataUri, hash_1, parsed_1, error_2;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    set(function (state) { state.isUploading = true; });
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 4, 5, 6]);
                    return [4 /*yield*/, get().compressImage(image)];
                case 2:
                    dataUri = _a.sent();
                    return [4 /*yield*/, get().calculateHash(image.name + dataUri + answerId)];
                case 3:
                    hash_1 = _a.sent();
                    parsed_1 = imageSchema.safeParse({
                        name: image.name,
                        dataUri: dataUri,
                        answerId: answerId,
                        hash: hash_1,
                    });
                    if (!parsed_1.success) {
                        console.error('Invalid image data:', parsed_1.error);
                        throw new Error('Invalid image data.');
                    }
                    set(function (state) {
                        state.images.set(hash_1, parsed_1.data);
                    });
                    return [2 /*return*/, parsed_1.data];
                case 4:
                    error_2 = _a.sent();
                    console.error('Error adding image:', error_2);
                    toast.error(__('app.toasts.error_adding_image'));
                    throw error_2;
                case 5:
                    set(function (state) { state.isUploading = false; });
                    return [7 /*endfinally*/];
                case 6: return [2 /*return*/];
            }
        });
    }); },
    removeImage: function (hash) {
        set(function (state) {
            state.images.delete(hash);
        });
    },
    pushImageToBucket: function (hash) { return __awaiter(void 0, void 0, void 0, function () {
        var image, formData, url, err_1, error;
        return __generator(this, function (_a) {
            switch (_a.label) {
                case 0:
                    if (!window.navigator.onLine) {
                        return [2 /*return*/, {
                                success: false,
                                error: new Error('User is offline. Image cannot be pushed.')
                            }];
                    }
                    image = get().images.get(hash);
                    if (!image) {
                        return [2 /*return*/, {
                                success: false,
                                error: new Error('Image not found.')
                            }];
                    }
                    // Early return if the image has already been pushed to the bucket
                    if (image.pushedToBucket) {
                        return [2 /*return*/, {
                                success: true,
                                error: null,
                            }];
                    }
                    formData = new FormData();
                    formData.append('image', get().decompressImage(image.dataUri, image.name));
                    formData.append('hash', hash);
                    _a.label = 1;
                case 1:
                    _a.trys.push([1, 3, , 4]);
                    url = apiRoutes.inspections.image.pushToBucket.replace(':answer', image.answerId.toString());
                    return [4 /*yield*/, window.axios.post(url, formData, {
                            headers: {
                                'Content-Type': 'multipart/form-data',
                            }
                        })];
                case 2:
                    _a.sent();
                    return [3 /*break*/, 4];
                case 3:
                    err_1 = _a.sent();
                    error = err_1;
                    console.error('Error pushing image to bucket:', error.message);
                    return [2 /*return*/, {
                            success: false,
                            error: new Error("Error pushing image to bucket: ".concat(error.message))
                        }];
                case 4:
                    // Update the image in the store
                    set(function (state) {
                        var updated = state.images.get(hash);
                        if (updated) {
                            updated.pushedToBucket = true;
                            state.images.set(hash, updated);
                        }
                    });
                    return [2 /*return*/, { success: true, error: null }];
            }
        });
    }); }
}); }), {
    name: 'bsp:image-store',
    storage: createJSONStorage(function () { return ({
        getItem: function (name) { return __awaiter(void 0, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, localforage.getItem(name)];
                    case 1: return [2 /*return*/, _a.sent()];
                }
            });
        }); },
        setItem: function (name, value) { return __awaiter(void 0, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, localforage.setItem(name, value)];
                    case 1: return [2 /*return*/, _a.sent()];
                }
            });
        }); },
        removeItem: function (name) { return __awaiter(void 0, void 0, void 0, function () {
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0: return [4 /*yield*/, localforage.removeItem(name)];
                    case 1: return [2 /*return*/, _a.sent()];
                }
            });
        }); },
    }); }),
    partialize: function (state) { return ({
        images: Array.from(state.images.values()),
    }); },
    merge: function (persisted, current) {
        var merged = merge(current, persisted);
        merged.images = new Map(merged.images.map(function (image) { return [image.hash, image]; }));
        return merged;
    },
}));
